/**
 * Archivio dei file media, con quattro implementazioni reali (+ memoria) dietro la stessa interfaccia, scelte da
 * `openAssetStore` in base a ciò che il contesto offre davvero (in http su un IP della LAN, cioè contesto NON
 * sicuro, mancano `caches`, OPFS, `serviceWorker`, `crypto.subtle`: resta IndexedDB):
 *   OPFS -> Cache API -> IdbAssetStore (Blob in IndexedDB) -> MemoryAssetStore.
 *   - OpfsAssetStore      Origin Private File System (preferita): file veri su disco, scrittura in streaming
 *                         su un file temporaneo `.part`, poi rename atomico (`FileSystemFileHandle.move`).
 *   - CacheApiAssetStore  fallback per WebView/TV senza OPFS: Cache API, corpo in streaming, validità
 *                         sancita da un record "meta" scritto solo DOPO la verifica dello sha256.
 *   - MemoryAssetStore    solo per i test.
 *
 * Un asset è identificato da (id, sha256): se il server cambia il contenuto di un id, lo sha256 nuovo
 * è un file nuovo, e il vecchio resta finché il GC non lo rimuove.
 * `getObjectUrl` restituisce un URL `blob:` che punta al File su disco (nessun file grande in RAM);
 * chi lo riceve deve chiamare `URL.revokeObjectURL` quando ha finito.
 */

export interface AssetKey {
  id: string;
  sha256: string;
}
export interface StoredAsset extends AssetKey {
  size: number;
}
export interface AssetMeta {
  mime: string;
  size: number;
}
export interface AssetWriter {
  write(chunk: Uint8Array): Promise<void>;
  /** Da chiamare SOLO dopo aver verificato lo sha256: rende l'asset visibile (atomico). */
  commit(): Promise<void>;
  /** Scarta tutto quanto scritto finora. */
  abort(): Promise<void>;
}
export interface StorageEstimate {
  usage: number;
  quota: number;
}
export interface AssetStore {
  readonly kind: 'opfs' | 'cache-api' | 'idb' | 'memory';
  list(): Promise<StoredAsset[]>;
  begin(key: AssetKey, meta: AssetMeta): Promise<AssetWriter>;
  getObjectUrl(key: AssetKey): Promise<string>;
  remove(key: AssetKey): Promise<void>;
  clear(): Promise<void>;
  estimate(): Promise<StorageEstimate | null>;
}

export const keyString = (k: AssetKey): string => `${k.id}|${k.sha256}`;

async function storageEstimate(): Promise<StorageEstimate | null> {
  try {
    const e = await navigator.storage.estimate();
    if (typeof e.quota === 'number' && typeof e.usage === 'number' && e.quota > 0) return { usage: e.usage, quota: e.quota };
  } catch { /* non supportato */ }
  return null;
}

// ---------------------------------------------------------------------------
// Memory (test)
// ---------------------------------------------------------------------------

export class MemoryAssetStore implements AssetStore {
  readonly kind = 'memory' as const;
  readonly files = new Map<string, { chunks: BlobPart[]; size: number; mime: string }>();
  quota: StorageEstimate | null = null;

  /** Inserisce un Blob già verificato (usato da IdbAssetStore quando la quota si esaurisce). */
  putBlob(key: AssetKey, blob: Blob, mime: string): void {
    this.files.set(keyString(key), { chunks: [blob], size: blob.size, mime });
  }

  async list(): Promise<StoredAsset[]> {
    return [...this.files.entries()].map(([k, v]) => {
      const [id, sha256] = k.split('|') as [string, string];
      return { id, sha256, size: v.size };
    });
  }

  async begin(key: AssetKey, meta: AssetMeta): Promise<AssetWriter> {
    const chunks: BlobPart[] = [];
    let size = 0;
    return {
      write: async (c) => { chunks.push(c.slice()); size += c.length; },
      commit: async () => { this.files.set(keyString(key), { chunks, size, mime: meta.mime }); },
      abort: async () => { chunks.length = 0; },
    };
  }

  async getObjectUrl(key: AssetKey): Promise<string> {
    const f = this.files.get(keyString(key));
    if (!f) throw new Error(`asset assente: ${key.id}`);
    if (typeof URL.createObjectURL === 'function') return URL.createObjectURL(new Blob(f.chunks, { type: f.mime }));
    return `memory:${key.id}`;
  }

  async remove(key: AssetKey): Promise<void> { this.files.delete(keyString(key)); }
  async clear(): Promise<void> { this.files.clear(); }
  async estimate(): Promise<StorageEstimate | null> { return this.quota; }
}

// ---------------------------------------------------------------------------
// OPFS
// ---------------------------------------------------------------------------

export interface FsWritable {
  write(data: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}
export interface FsFileHandle {
  readonly kind: 'file';
  getFile(): Promise<File>;
  createWritable(): Promise<FsWritable>;
  /**
   * Due firme: `move(newName)` esiste solo in Chromium; `move(destinationDirectory, newName)` esiste in Chromium E in
   * WebKit (Safari), dove la forma a un argomento lancia "Not enough arguments". Si usa sempre quella a due argomenti.
   */
  move?(destinationDirectory: FsDirHandle, newName: string): Promise<void>;
  move?(newName: string): Promise<void>;
}
export interface FsDirHandle {
  readonly kind: 'directory';
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<FsFileHandle>;
  getDirectoryHandle(name: string, opts?: { create?: boolean }): Promise<FsDirHandle>;
  removeEntry(name: string, opts?: { recursive?: boolean }): Promise<void>;
  entries(): AsyncIterableIterator<[string, FsFileHandle | FsDirHandle]>;
}

const OPFS_DIR = 'hoox-assets';
const FINAL_RE = /^(.*)\.([0-9a-f]{64})\.bin$/;

/** OPFS usabile con rename atomico e scrittura in streaming? (Chromium >= 110) */
export function opfsSupported(): boolean {
  const storage = (typeof navigator !== 'undefined' ? navigator.storage : undefined) as { getDirectory?: unknown } | undefined;
  const proto = (globalThis as { FileSystemFileHandle?: { prototype: object } }).FileSystemFileHandle?.prototype;
  return typeof storage?.getDirectory === 'function'
    && !!proto && 'createWritable' in proto && 'move' in proto;
}

export class OpfsAssetStore implements AssetStore {
  readonly kind = 'opfs' as const;
  /** Asset verificati che né il rename né la copia sono riusciti a mettere su OPFS: restano in RAM e si riproducono. */
  readonly memory = new MemoryAssetStore();
  private readonly reported = new Set<string>();

  private constructor(private readonly dir: FsDirHandle, private readonly onError: (msg: string) => void) {}

  static async open(onError: (msg: string) => void = () => undefined): Promise<OpfsAssetStore> {
    const root = (await (navigator.storage as unknown as { getDirectory(): Promise<FsDirHandle> }).getDirectory());
    return OpfsAssetStore.fromDirectory(await root.getDirectoryHandle(OPFS_DIR, { create: true }), onError);
  }

  /** Su una cartella già aperta (anche finta, per i test). */
  static async fromDirectory(dir: FsDirHandle, onError: (msg: string) => void = () => undefined): Promise<OpfsAssetStore> {
    const store = new OpfsAssetStore(dir, onError);
    await store.dropPartials();
    return store;
  }

  private reportOnce(key: AssetKey, e: unknown): void {
    const k = keyString(key);
    if (this.reported.has(k)) return;
    this.reported.add(k);
    this.onError(`OPFS: salvataggio di ${key.id} fallito (${e instanceof Error ? `${e.name}: ${e.message}` : String(e)}): resta solo in memoria`);
  }

  /** `.part` -> nome finale. Prima il rename atomico a due argomenti; poi la copia (createWritable è atomico alla close). */
  private async promote(tmp: FsFileHandle, tmpName: string, finalName: string): Promise<void> {
    try {
      if (typeof tmp.move !== 'function') throw new Error('move() assente');
      await tmp.move(this.dir, finalName);
      return;
    } catch { /* ripiego: copia */ }
    const src = await tmp.getFile();
    let ww: FsWritable | null = null;
    try {
      const dst = await this.dir.getFileHandle(finalName, { create: true });
      ww = await dst.createWritable();
      const reader = src.stream().getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        await ww.write(value);
      }
      await ww.close();
    } catch (e) {
      if (ww) await ww.abort().catch(() => undefined);
      await this.dir.removeEntry(finalName).catch(() => undefined); // mai un file finale a metà (né vuoto)
      throw e;
    }
    await this.dir.removeEntry(tmpName).catch(() => undefined);
  }

  private static finalName(k: AssetKey): string {
    return `${encodeURIComponent(k.id)}.${k.sha256}.bin`;
  }

  /** Scarta i .part lasciati da un crash o da uno spegnimento a metà download. */
  private async dropPartials(): Promise<void> {
    for await (const [name, h] of this.dir.entries()) {
      if (h.kind === 'file' && name.endsWith('.part')) await this.dir.removeEntry(name).catch(() => undefined);
    }
  }

  async list(): Promise<StoredAsset[]> {
    const out = new Map<string, StoredAsset>();
    for await (const [name, h] of this.dir.entries()) {
      const m = FINAL_RE.exec(name);
      if (h.kind === 'file' && m) {
        const f = await h.getFile();
        const a = { id: decodeURIComponent(m[1] as string), sha256: m[2] as string, size: f.size };
        out.set(keyString(a), a);
      }
    }
    for (const a of await this.memory.list()) out.set(keyString(a), a);
    return [...out.values()];
  }

  async begin(key: AssetKey, meta: AssetMeta): Promise<AssetWriter> {
    const finalName = OpfsAssetStore.finalName(key);
    const tmpName = `${finalName}.part`;
    const handle = await this.dir.getFileHandle(tmpName, { create: true });
    const w = await handle.createWritable(); // tronca: ricomincia da zero
    let closed = false;
    return {
      write: (c) => w.write(c),
      commit: async () => {
        await w.close();
        closed = true;
        try {
          await this.promote(handle, tmpName, finalName);
        } catch (e) {
          // Il download è già verificato: non si butta. Si legge il .part in RAM e si riproduce da lì.
          this.reportOnce(key, e);
          const buf = await (await handle.getFile()).arrayBuffer();
          this.memory.putBlob(key, new Blob([buf], { type: meta.mime }), meta.mime);
          await this.dir.removeEntry(tmpName).catch(() => undefined);
        }
      },
      abort: async () => {
        if (!closed) await w.abort().catch(() => undefined);
        await this.dir.removeEntry(tmpName).catch(() => undefined);
      },
    };
  }

  async getObjectUrl(key: AssetKey): Promise<string> {
    if (this.memory.files.has(keyString(key))) return this.memory.getObjectUrl(key);
    const h = await this.dir.getFileHandle(OpfsAssetStore.finalName(key));
    return URL.createObjectURL(await h.getFile());
  }

  async remove(key: AssetKey): Promise<void> {
    this.reported.delete(keyString(key));
    await this.memory.remove(key);
    await this.dir.removeEntry(OpfsAssetStore.finalName(key)).catch(() => undefined);
  }

  async clear(): Promise<void> {
    this.reported.clear();
    await this.memory.clear();
    const names: string[] = [];
    for await (const [name] of this.dir.entries()) names.push(name);
    for (const n of names) await this.dir.removeEntry(n).catch(() => undefined);
  }

  estimate(): Promise<StorageEstimate | null> { return storageEstimate(); }
}

// ---------------------------------------------------------------------------
// Cache API (fallback)
// ---------------------------------------------------------------------------

const CACHE_NAME = 'hoox-assets-v1';

export class CacheApiAssetStore implements AssetStore {
  readonly kind = 'cache-api' as const;
  private constructor(private readonly cache: Cache) {}

  static async open(): Promise<CacheApiAssetStore> {
    return new CacheApiAssetStore(await caches.open(CACHE_NAME));
  }

  private static base(): string {
    const o = typeof location !== 'undefined' && location.origin !== 'null' ? location.origin : 'https://player.invalid';
    return `${o}/__assets__/`;
  }
  private static dataUrl(k: AssetKey): string { return `${this.base()}data/${encodeURIComponent(k.id)}/${k.sha256}`; }
  private static metaUrl(k: AssetKey): string { return `${this.base()}meta/${encodeURIComponent(k.id)}/${k.sha256}`; }

  async list(): Promise<StoredAsset[]> {
    const out: StoredAsset[] = [];
    const prefix = `${CacheApiAssetStore.base()}meta/`;
    for (const req of await this.cache.keys()) {
      if (!req.url.startsWith(prefix)) continue;
      const [idEnc, sha256] = req.url.slice(prefix.length).split('/') as [string, string];
      const res = await this.cache.match(req);
      const size = res ? Number((await res.json() as { size?: number }).size ?? 0) : 0;
      out.push({ id: decodeURIComponent(idEnc), sha256, size });
    }
    return out;
  }

  async begin(key: AssetKey, meta: AssetMeta): Promise<AssetWriter> {
    const url = CacheApiAssetStore.dataUrl(key);
    await this.cache.delete(CacheApiAssetStore.metaUrl(key)); // finché non è verificato non è "valido"
    let ctrl!: ReadableStreamDefaultController<Uint8Array>;
    let waiter: (() => void) | null = null;
    const body = new ReadableStream<Uint8Array>({
      start(c) { ctrl = c; },
      pull() { waiter?.(); waiter = null; }, // il consumatore (cache.put) chiede altro: sblocca write()
    }, { highWaterMark: 0 });
    const headers: Record<string, string> = { 'Content-Type': meta.mime || 'application/octet-stream' };
    const putting = this.cache.put(url, new Response(body, { headers }));
    putting.catch(() => undefined);
    return {
      write: (chunk) => new Promise<void>((resolve, reject) => {
        waiter = resolve;
        try { ctrl.enqueue(chunk.slice()); } catch (e) { reject(e); }
      }),
      commit: async () => {
        ctrl.close();
        await putting;
        await this.cache.put(
          CacheApiAssetStore.metaUrl(key),
          new Response(JSON.stringify({ size: meta.size }), { headers: { 'Content-Type': 'application/json' } }),
        );
      },
      abort: async () => {
        try { ctrl.error(new Error('abort')); } catch { /* già chiuso */ }
        await putting.catch(() => undefined);
        await this.cache.delete(url);
      },
    };
  }

  async getObjectUrl(key: AssetKey): Promise<string> {
    const res = await this.cache.match(CacheApiAssetStore.dataUrl(key));
    if (!res) throw new Error(`asset assente: ${key.id}`);
    return URL.createObjectURL(await res.blob());
  }

  async remove(key: AssetKey): Promise<void> {
    await this.cache.delete(CacheApiAssetStore.metaUrl(key));
    await this.cache.delete(CacheApiAssetStore.dataUrl(key));
  }

  async clear(): Promise<void> {
    for (const req of await this.cache.keys()) await this.cache.delete(req);
  }

  estimate(): Promise<StorageEstimate | null> { return storageEstimate(); }
}

// ---------------------------------------------------------------------------
// IndexedDB (fallback per i contesti NON sicuri: http su IP di LAN, iPhone, ...)
// ---------------------------------------------------------------------------

const IDB_NAME = 'hoox-assets-v2'; // v2: blocchi ArrayBuffer (v1 salvava Blob, che WebKit rifiuta nelle sessioni effimere)
const IDB_BLOCKS = 'blobs';
const IDB_META = 'meta';
export const IDB_CHUNK_BYTES = 4 * 1024 * 1024;
const BLOB_CACHE_MAX_BYTES = 256 * 1024 * 1024;

interface IdbMetaRecord extends StoredAsset { mime: string; chunks: number }

const blockKey = (k: AssetKey, n: number): string => `${keyString(k)}#${n}`;

function idbReq<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('IndexedDB error'));
  });
}
function idbDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB error'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

/**
 * Asset come blocchi ArrayBuffer (~4 MiB) in IndexedDB, MAI come Blob: WebKit rifiuta i Blob negli IndexedDB delle
 * sessioni effimere (navigazione privata, contesti di test) con "BlobURLs are not yet supported".
 *  - record `blocks`: chiave `id|sha#n` -> ArrayBuffer; record `meta`: chiave `id|sha` -> {size, mime, chunks};
 *  - il commit scrive tutti i blocchi + il meta in UNA transazione, solo dopo la verifica dello sha256;
 *  - durante il download i blocchi stanno in RAM (limite noto: un asset = RAM per tutta la sua dimensione);
 *  - su QUALUNQUE errore di scrittura (quota, Blob non supportati, transazione abortita...) l'asset già scaricato e
 *    verificato resta in un MemoryAssetStore interno e viene riprodotto lo stesso; l'errore si segnala una volta per
 *    asset;
 *  - `getObjectUrl` ricostruisce il Blob dai blocchi e ne tiene una cache (LRU, 256 MiB): si cachea il Blob e non
 *    l'URL, perché chi riceve l'URL lo revoca quando ha finito.
 */
export class IdbAssetStore implements AssetStore {
  readonly kind = 'idb' as const;
  readonly memory = new MemoryAssetStore();
  private readonly reported = new Set<string>();
  private readonly blobCache = new Map<string, Blob>();

  private constructor(
    private readonly db: IDBDatabase,
    private readonly onError: (msg: string) => void,
    private readonly chunkBytes: number,
  ) {}

  static async open(
    factory: IDBFactory = indexedDB,
    onError: (msg: string) => void = () => undefined,
    chunkBytes = IDB_CHUNK_BYTES,
  ): Promise<IdbAssetStore> {
    const r = factory.open(IDB_NAME, 1);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains(IDB_BLOCKS)) db.createObjectStore(IDB_BLOCKS);
      if (!db.objectStoreNames.contains(IDB_META)) db.createObjectStore(IDB_META);
    };
    return new IdbAssetStore(await idbReq(r), onError, chunkBytes);
  }

  async list(): Promise<StoredAsset[]> {
    let metas: IdbMetaRecord[] = [];
    try {
      metas = await idbReq(this.db.transaction(IDB_META, 'readonly').objectStore(IDB_META).getAll()) as IdbMetaRecord[];
    } catch { /* DB illeggibile: restano solo gli asset in memoria */ }
    const out = new Map<string, StoredAsset>();
    for (const m of [...metas, ...(await this.memory.list())]) out.set(keyString(m), { id: m.id, sha256: m.sha256, size: m.size });
    return [...out.values()];
  }

  private reportOnce(key: AssetKey, e: unknown): void {
    const k = keyString(key);
    if (this.reported.has(k)) return;
    this.reported.add(k);
    this.onError(`IndexedDB: scrittura di ${key.id} fallita (${e instanceof Error ? `${e.name}: ${e.message}` : String(e)}): resta solo in memoria`);
  }

  private cacheBlob(k: string, blob: Blob): void {
    this.blobCache.delete(k);
    this.blobCache.set(k, blob);
    let total = 0;
    for (const b of this.blobCache.values()) total += b.size;
    for (const old of [...this.blobCache.keys()]) {
      if (total <= BLOB_CACHE_MAX_BYTES || this.blobCache.size <= 1) break;
      if (old === k) continue;
      total -= this.blobCache.get(old)?.size ?? 0;
      this.blobCache.delete(old);
    }
  }

  async begin(key: AssetKey, meta: AssetMeta): Promise<AssetWriter> {
    const blocks: ArrayBuffer[] = [];
    let cur = new Uint8Array(this.chunkBytes);
    let fill = 0;
    let size = 0;
    const reset = () => { blocks.length = 0; cur = new Uint8Array(0); fill = 0; size = 0; };
    return {
      write: async (c) => {
        let off = 0;
        while (off < c.length) {
          if (fill === cur.length) { // blocco pieno: chiudilo e ricomincia
            blocks.push(cur.buffer as ArrayBuffer);
            cur = new Uint8Array(this.chunkBytes);
            fill = 0;
          }
          const n = Math.min(c.length - off, cur.length - fill);
          cur.set(c.subarray(off, off + n), fill);
          fill += n;
          off += n;
        }
        size += c.length;
      },
      commit: async () => {
        const all = [...blocks, ...(fill > 0 ? [cur.slice(0, fill).buffer as ArrayBuffer] : [])];
        const mime = meta.mime;
        try {
          const tx = this.db.transaction([IDB_BLOCKS, IDB_META], 'readwrite');
          all.forEach((b, n) => tx.objectStore(IDB_BLOCKS).put(b, blockKey(key, n)));
          tx.objectStore(IDB_META).put({ id: key.id, sha256: key.sha256, size, mime, chunks: all.length } satisfies IdbMetaRecord, keyString(key));
          await idbDone(tx); // tutto o niente
        } catch (e) {
          // qualunque errore di scrittura: il download è già verificato, non si butta. Resta in memoria e si riproduce.
          this.reportOnce(key, e);
          this.memory.putBlob(key, new Blob(all, { type: mime }), mime);
        } finally {
          reset();
        }
      },
      abort: async () => { reset(); },
    };
  }

  async getObjectUrl(key: AssetKey): Promise<string> {
    const k = keyString(key);
    if (this.memory.files.has(k)) return this.memory.getObjectUrl(key);
    let blob = this.blobCache.get(k);
    if (blob) {
      this.cacheBlob(k, blob); // LRU: lo rimette in coda
    } else {
      const meta = await idbReq(this.db.transaction(IDB_META, 'readonly').objectStore(IDB_META).get(k)) as IdbMetaRecord | undefined;
      if (!meta) throw new Error(`asset assente: ${key.id}`);
      const store = this.db.transaction(IDB_BLOCKS, 'readonly').objectStore(IDB_BLOCKS);
      const parts = await Promise.all(Array.from({ length: meta.chunks }, (_, n) => idbReq(store.get(blockKey(key, n)))));
      if (parts.some((p) => p === undefined)) throw new Error(`blocchi mancanti per ${key.id}`);
      blob = new Blob(parts as ArrayBuffer[], { type: meta.mime });
      this.cacheBlob(k, blob);
    }
    return URL.createObjectURL(blob);
  }

  private async deleteFromDb(keys: AssetKey[] | 'all'): Promise<void> {
    if (keys === 'all') {
      const tx = this.db.transaction([IDB_BLOCKS, IDB_META], 'readwrite');
      tx.objectStore(IDB_BLOCKS).clear();
      tx.objectStore(IDB_META).clear();
      await idbDone(tx);
      return;
    }
    for (const key of keys) {
      const meta = await idbReq(this.db.transaction(IDB_META, 'readonly').objectStore(IDB_META).get(keyString(key))) as IdbMetaRecord | undefined;
      const tx = this.db.transaction([IDB_BLOCKS, IDB_META], 'readwrite');
      for (let n = 0; n < (meta?.chunks ?? 0); n++) tx.objectStore(IDB_BLOCKS).delete(blockKey(key, n));
      tx.objectStore(IDB_META).delete(keyString(key));
      await idbDone(tx);
    }
  }

  async remove(key: AssetKey): Promise<void> {
    this.blobCache.delete(keyString(key));
    this.reported.delete(keyString(key));
    await this.memory.remove(key);
    await this.deleteFromDb([key]).catch(() => undefined); // DB rotto: non bloccare il GC
  }

  async clear(): Promise<void> {
    this.blobCache.clear();
    this.reported.clear();
    await this.memory.clear();
    await this.deleteFromDb('all').catch(() => undefined);
  }

  estimate(): Promise<StorageEstimate | null> { return storageEstimate(); }
}

// ---------------------------------------------------------------------------
// Scelta dello store
// ---------------------------------------------------------------------------

const errMsg = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/**
 * Sceglie lo store guardando cosa il contesto offre DAVVERO (mai assumere che `caches` esista: in un contesto
 * non sicuro è undefined e usarlo lancia ReferenceError). Ordine: OPFS -> Cache API -> IndexedDB -> memoria.
 * Ogni tentativo di apertura è in try/catch; i motivi dei ripieghi vanno a `note`.
 */
export async function openAssetStore(note: (msg: string) => void = () => undefined): Promise<AssetStore> {
  if (opfsSupported()) {
    try {
      return await OpfsAssetStore.open(note);
    } catch (e) {
      note(`OPFS non utilizzabile: ${errMsg(e)}`);
    }
  }
  if (typeof caches !== 'undefined') {
    try {
      return await CacheApiAssetStore.open();
    } catch (e) {
      note(`Cache API non utilizzabile: ${errMsg(e)}`);
    }
  }
  if (typeof indexedDB !== 'undefined') {
    try {
      return await IdbAssetStore.open(indexedDB, note);
    } catch (e) {
      note(`IndexedDB per i media non utilizzabile: ${errMsg(e)}`);
    }
  }
  note('nessuno storage persistente per i media: solo memoria (si perdono al riavvio)');
  return new MemoryAssetStore();
}
